import { ApiConfig } from "../apiConfig/apiConfig";
import { apiDelete, apiDeleteWithBody, apiGet, apiGetBlob, apiGetWithMeta, apiPost, apiPostForm, apiPostWithMeta, apiPut, apiPutForm } from "../apiConfig/apiCall";

const AdminService = {
  /**
   * Get admin activity logs
   * @param {Object} params - { page, limit, admin_id, module, action }
   */
  getAdminLogs: async (params = {}) => {
    const { baseAdmin, adminLogs } = ApiConfig;
    const url = baseAdmin + adminLogs;
    return apiGet(url, {}, params);
  },

  /**
   * Get dashboard counts (all values in USDT)
   */
  getDashboardCounts: async () => {
    const { baseAdmin, dashboardCounts } = ApiConfig;
    const url = baseAdmin + dashboardCounts;
    return apiGet(url);
  },

  /**
   * Get user list (traders)
   * @param {Object} params - { page, limit, search, kyc_status, status, country_code }
   */
  getUsers: async (params = {}) => {
    const { baseAdmin, adminUsers } = ApiConfig;
    const url = baseAdmin + adminUsers;
    return apiGet(url, {}, params);
  },

  /**
   * Blocked / filtered user list (admin)
   * @param {Object} params - { page, limit, search, kyc_status, account_status, country_code }
   */
  getBlockedUsers: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.usersBlockedList, {}, params),

  /**
   * Unverified user list (admin)
   * @param {Object} params - { page, limit, search, kyc_status, status }
   */
  getUnverifiedUsers: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.usersUnverifiedList, {}, params),

  getUserDetail: (userId) => apiGet(ApiConfig.baseAdmin + ApiConfig.userDetail(userId)),

  /** DELETE /v1/admin/users/account — body: { userId, confirm: "DELETE" } */
  deleteUserAccount: (userId) =>
    apiDeleteWithBody(ApiConfig.baseAdmin + ApiConfig.usersDeleteAccount, {
      userId,
      confirm: "DELETE",
    }),

  /** Commission report list. Params: page, limit, search, account_status, from, to, commission_type, package, referral_level, sort_by, sort_order */
  getUsersCommissionReport: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.usersCommissionReport, {}, params),

  /** User commission report overview (summary + 5-row previews) */
  getUserCommissionReport: (userId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.userCommissionReport(userId)),

  /** User staking packages that generated upline commission. Params: page, limit, from, to */
  getUserCommissionStakingDetails: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.userCommissionStakingDetails(userId), {}, params),

  /** User trades that generated upline commission. Params: page, limit, from, to */
  getUserCommissionTradingDetails: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.userCommissionTradingDetails(userId), {}, params),

  /** Commission this user earned from downline. Params: page, limit, from, to, type */
  getUserCommissionDownline: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.userCommissionDownline(userId), {}, params),

  /** Commission this user's activity paid to uplines. Params: page, limit, from, to, type */
  getUserCommissionUpline: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.userCommissionUpline(userId), {}, params),

  getUserKyc: (userId) => apiGet(ApiConfig.baseAdmin + ApiConfig.userKyc(userId)),
  /**
   * Get KYC list (all users)
   * @param {Object} params - { status, page, limit, search, country_code }
   * status: pending | approved | rejected | all
   */
  getKycList: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.kycList, {}, params),
  /** Active / Inactive trading profile — optional `extra` merges into body. */
  updateUserStatus: (userId, status, extra = {}) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.userStatus, { userId, status, ...extra }),
  /** PUT users/unblock — body: { userId, status: "Active" | "Blocked" } (account block/unblock). */
  updateUserAccountBlockStatus: (userId, status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.userUnblock, { userId, status }),
  /** PUT users/payout-status — body: { userId, payout_status: "active" | "inactive" } */
  updateUserPayoutStatus: (userId, payout_status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.userPayoutStatus, { userId, payout_status }),
  /**
   * PUT users/contact — body: { userId, emailId?, mobileNumber?, country_code? }
   * At least one of emailId / mobileNumber required.
   */
  updateUserContact: (body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.usersContact, body),
  /** PUT users/brokerage-fee — body: { userId, brokerageFee: boolean } */
  updateUserBrokerageFee: (userId, brokerageFee) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.usersBrokerageFee, { userId, brokerageFee }),
  /** PUT users/withdrawal-status — body: { userId, withdrawalEnabled: boolean } */
  updateUserWithdrawalStatus: (userId, withdrawalEnabled) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.usersWithdrawalStatus, { userId, withdrawalEnabled }),
  approveKyc: (userId, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycApprove, { userId, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  rejectKyc: (userId, rejectReason, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycReject, { userId, reject_reason: rejectReason, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  approveKycDocument: (userId, documentType, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycApproveDocument, { userId, document_type: documentType, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  rejectKycDocument: (userId, documentType, rejectReason, adminNotes = "") =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.kycRejectDocument, { userId, document_type: documentType, reject_reason: rejectReason, ...(adminNotes?.trim() && { admin_notes: adminNotes.trim() }) }),
  getUserWallets: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userWallets(userId), {}, params),
  getUserTransactions: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userTransactions(userId), {}, params),
  getUserInternalTransfers: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userInternalTransfers(userId), {}, params),
  /** GET /v1/admin/users/:userId/internal-transfer-settings */
  getUserInternalTransferSettings: (userId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.userInternalTransferSettings(userId)),
  /**
   * PUT /v1/admin/users/internal-transfer-settings
   * One toggle: { userId, fromWallet, toWallet, enabled }
   * Wallet master: { userId, fromWallet, enabled } (no toWallet)
   * Save-all: { userId, disabled_wallets, disabled_routes } — do not mix with one-toggle fields
   */
  updateUserInternalTransferSettings: (body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.usersInternalTransferSettings, body),
  getUserSpotTrades: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSpotTrades(userId), {}, params),
  getUserSpotOrders: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSpotOrders(userId), {}, params),
  getUserSwapHistory: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSwapHistory(userId), {}, params),
  getUserEarningPlans: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userEarningPlans(userId), {}, params),
  getUserLaunchpad: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userLaunchpad(userId), {}, params),
  getUserSupportTickets: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userSupportTickets(userId), {}, params),
  getUserReferral: (userId) => apiGet(ApiConfig.baseAdmin + ApiConfig.userReferral(userId)),
  getUserP2pOrders: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userP2pOrders(userId), {}, params),
  getUserP2pAds: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userP2pAds(userId), {}, params),
  getUserP2pPaymentMethods: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userP2pPaymentMethods(userId), {}, params),
  getUserFuturesTrades: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userFuturesTrades(userId), {}, params),
  getUserFuturesOrders: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userFuturesOrders(userId), {}, params),
  getUserFuturesPositions: (userId, params) => apiGet(ApiConfig.baseAdmin + ApiConfig.userFuturesPositions(userId), {}, params),

  /**
   * GET /v1/admin/copy-trading/master-traders
   * @param {Object} params - { page, limit, search, role: "master" | "none" | "all" }
   */
  getMasterTraders: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterTraders, {}, params),

  /**
   * GET /v1/admin/copy-trading/master-applications
   * @param {Object} params - { page, limit, search, status: pending|contacted|converted|rejected|all }
   */
  getCopyTradingMasterApplications: (params = {}) =>
    apiGetWithMeta(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterApplications, {}, params),

  /** GET /v1/admin/copy-trading/master-applications/:applicationId */
  getCopyTradingMasterApplicationById: (applicationId) =>
    apiGetWithMeta(
      ApiConfig.baseAdmin + ApiConfig.copyTradingMasterApplicationById(applicationId)
    ),

  /**
   * PUT /v1/admin/copy-trading/master-applications/:applicationId/status
   * Tracks the lead only — does not promote/demote the master role.
   */
  updateCopyTradingMasterApplicationStatus: (applicationId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterApplicationStatus(applicationId), body),

  /**
   * PUT /v1/admin/copy-trading/master-trade-account
   * Demotion with active followers runs a wind-down. Optional extras: { force, reason }.
   */
  updateMasterTradeAccount: (userId, isMasterAccount, extras = {}) => {
    const body = { userId, isMasterAccount };
    if (!isMasterAccount) {
      if (extras.reason) body.reason = extras.reason;
      if (extras.force) body.force = true;
    }
    return apiPut(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterTradeAccount, body);
  },

  /**
   * GET /v1/admin/copy-trading/masters/:masterId/configuration
   * 404 when the master has no config yet.
   */
  getMasterTraderConfiguration: (masterId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterConfiguration(masterId)),

  /**
   * PUT /v1/admin/copy-trading/masters/:masterId/configuration
   * Upsert — create on first call, merge-update afterwards.
   */
  upsertMasterTraderConfiguration: (masterId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterConfiguration(masterId), body),

  /**
   * GET /v1/admin/copy-trading/masters/:masterId/followers
   * @param {Object} params - { page, limit, status?: "ACTIVE" | "PAUSED" | "STOPPED" }
   */
  getMasterTraderFollowers: (masterId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterFollowers(masterId), {}, params),

  /**
   * GET /v1/admin/copy-trading/masters/:masterId/trades
   * @param {Object} params - { market: "SPOT" | "FUTURES", page, limit, status }
   */
  getMasterTraderTrades: (masterId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterTrades(masterId), {}, params),

  /** GET /v1/admin/copy-trading/masters/:masterId/pnl */
  getMasterTraderPnl: (masterId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterPnl(masterId)),

  /**
   * GET /v1/admin/copy-trading/masters/:masterId/copied-trades
   * @param {Object} params - { page, limit }
   */
  getMasterCopiedTrades: (masterId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterCopiedTrades(masterId), {}, params),

  /**
   * GET /v1/admin/copy-trading/masters/:masterId/trades/pnl
   * @param {Object} params - { market?: "SPOT" | "FUTURES", page, limit }
   */
  getMasterTraderTradesPnl: (masterId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterTradesPnl(masterId), {}, params),

  /**
   * GET /v1/admin/copy-trading/masters/:masterId/followers/pnl
   * @param {Object} params - { status?: "ACTIVE" | "PAUSED" | "STOPPED" | "all" }
   */
  getMasterFollowersPnl: (masterId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterFollowersPnl(masterId), {}, params),

  getCopyTradingSnapshots: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingSnapshots, {}, params),

  getCopyTradingRiskFlags: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingRiskFlags, {}, params),

  releaseMasterTotalStop: (masterId) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.copyTradingReleaseTotalStop(masterId), {}),

  getCopyTradingAlerts: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingAlerts, {}, params),

  acknowledgeCopyTradingAlert: (alertId) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.copyTradingAcknowledgeAlert(alertId), {}),

  getCopyTradingSettlements: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingSettlements, {}, params),

  runCopyTradingSettlements: (body = {}) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.copyTradingSettlementsRun, body),

  getMasterWindDown: (masterId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterWindDown(masterId)),

  getCopyTradingReconciliation: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingReconciliation, {}, params),

  runCopyTradingReconciliation: (body = {}) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.copyTradingReconciliationRun, body),

  getMasterConfigVersions: (masterId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterConfigVersions(masterId), {}, params),

  getMasterGrandfatheredFollowers: (masterId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingMasterGrandfatheredFollowers(masterId)),

  getCopyTradingSurveillance: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.copyTradingSurveillance, {}, params),

  /**
   * Get global spot orders (orderbook view)
   * @param {Object} params - { page, limit, base_currency_id, quote_currency_id, side, status, user }
   */
  getGlobalSpotOrders: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.globalSpotOrders, {}, params),

  /**
   * Get global spot trades
   * @param {Object} params - { page, limit, base_currency_id, quote_currency_id, side, from, to, user }
   */
  getGlobalSpotTrades: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.globalSpotTrades, {}, params),

  /**
   * Global futures orders. Params: page, limit, pair_id, symbol, side, status, order_id, reduce_only, close_order, user
   */
  getGlobalFuturesOrders: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.globalFuturesOrders, {}, params),

  /**
   * Global futures positions. Params: page, limit, pair_id, position_id, symbol, side, status, liquidated, user
   */
  getGlobalFuturesPositions: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.globalFuturesPositions, {}, params),

  /**
   * Global futures trade history. Params: page, limit, pair_id, symbol, side, from, to, order_id, user
   */
  getGlobalFuturesTrades: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.globalFuturesTrades, {}, params),

  /** Global on-chain deposits (wallet_transaction DEPOSIT). Params: page, limit, user, currency_id, chain, short_name, status, from, to, transaction_hash, transfer_status, transfer_tx_hash */
  getGlobalWalletDeposits: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.walletDeposits, {}, params),

  /** Cobo deposit history. Params: page, limit, userId, status, assetId, chainId, txHash */
  getCoboDepositHistory: (params = {}) =>
    apiGet(ApiConfig.coboAdminDepositHistory, {}, params),

  /** Cobo deposit summary by currency. Params: status, credited, assetId, chainId, from, to */
  getCoboDepositSummaryByCurrency: (params = {}) =>
    apiGet(ApiConfig.coboAdminDepositSummaryByCurrency, {}, params),

  /** Cobo withdrawal history. Params: page, limit, userId, status, coin, chainId, txHash */
  getCoboWithdrawalHistory: (params = {}) =>
    apiGet(ApiConfig.coboAdminWithdrawalHistory, {}, params),

  /** POST /v1/cobo/withdrawal — body: adminApproved, withdrawalId; manual: approvalMode, adminTransactionHash */
  updateCoboWithdrawal: (payload) => apiPost(ApiConfig.coboWithdrawalAction, payload),

  /** Global gas top-up transfers. Params: page, limit, user, currency_id, chain, short_name, status, from, to, transaction_hash */
  getGlobalGasFeeTransfers: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.walletGasFeeTransfers, {}, params),

  /** Member → hot/admin wallet token transfers */
  getGlobalHotWalletTransfers: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.walletHotWalletTransfers, {}, params),

  /** Get coins config (available coins + supported chains with regex) */
  getCoinsConfig: () => apiGet(ApiConfig.baseAdmin + ApiConfig.coinsConfig),

  /** Get lightweight coins list (_id, name, short_name) */
  getCoinsList: () => apiGet(ApiConfig.baseAdmin + ApiConfig.coinsList),

  /** Get per-currency chain contract addresses & decimals */
  getCoinContractDetails: () => apiGet(ApiConfig.baseAdmin + ApiConfig.coinsContractDetails),

  /** GET /v1/admin/get-coin — { success, data: Coin[] } */
  getAdminGetCoinList: () => apiGet(ApiConfig.baseAdmin + ApiConfig.getCoin),

  /** Create new coin (multipart/form-data) */
  createCoin: (formData) => apiPostForm(ApiConfig.baseAdmin + ApiConfig.coinsCreate, formData),

  /** Update coin (multipart/form-data) */
  updateCoin: (coinId, formData) => apiPutForm(ApiConfig.baseAdmin + ApiConfig.coinsUpdate(coinId), formData),

  /** Get pair info for prefill (Binance/CMC/LOCAL) */
  getPairInfo: (body) => apiPost(ApiConfig.baseAdmin + ApiConfig.pairInfo, body),

  /** Create trading pair */
  createPair: (body) => apiPost(ApiConfig.baseAdmin + ApiConfig.pairsCreate, body),

  /** Get pairs list. Params: { status?: "Active" | "Inactive" } */
  getPairsList: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.pairsList, {}, params),

  /** List / Delist pair (update status: "Active" | "Inactive") */
  updatePairStatus: (pairId, status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.pairStatus(pairId), { status }),

  /** Update a spot pair (PUT /v1/admin/pairs/:pairId) */
  updateSpotPair: (pairId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.pairUpdate(pairId), body),

  /** Get futures pair info for prefill (Binance Futures) */
  getFuturesPairInfo: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.futuresPairInfo, body),

  /** Create futures trading pair */
  createFuturesPair: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.futuresPairsCreate, body),

  /** Create staking / earning package. Body: { currencyId, duration_days, return_percentage, min_amount, cancellation_fee? } */
  createEarningPackage: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.earningPackages, body),

  /** List earning packages. Params: page, limit, status (ACTIVE | INACTIVE | all), currency_id, currency */
  getEarningPackages: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningPackages, {}, params),

  /** Find one package by id (scans list pages; cap 50 pages × 100 rows). */
  fetchEarningPackageById: async (packageId) => {
    let page = 1;
    const limit = 100;
    let totalPages = 1;
    do {
      const res = await apiGet(
        ApiConfig.baseAdmin + ApiConfig.earningPackages,
        {},
        { page, limit, status: "all" }
      );
      const list = Array.isArray(res?.data) ? res.data : [];
      const found = list.find((p) => String(p._id || p.id) === String(packageId));
      if (found) return found;
      totalPages = res?.pagination?.total_pages || 1;
      page += 1;
    } while (page <= totalPages && page <= 50);
    return null;
  },

  /** Update earning package (at least one field). Body: duration_days, return_percentage, return_type, min_amount, cancellation_fee, level1_commission, level2_commission, level3_commission, payout_status, status, currencyId */
  updateEarningPackage: (packageId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.earningPackageById(packageId), body),

  /** Global user earning subscriptions. Params: page, limit, user, status, currency_id, plan_id, from, to */
  getGlobalUserEarningPlans: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningUserPlans, {}, params),

  /** Cancelled user earning plans only. Params: page, limit, user, currency_id, plan_id, from, to */
  getCancelledUserEarningPlans: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningUserPlansCancelled, {}, params),

  /** Admin-assigned user earning plans only. Params: page, limit, user (if supported) */
  getEarningUserPlansAdminForUser: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningUserPlansAdminForUser, {}, params),

  /** Payout history for one user stake. Body: { userId, stakingId } (stakingId = user plan _id) */
  getEarningUserPlanPayoutHistory: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.earningUserPlanPayoutHistory, body),

  /** Edit history for a subscription. GET /v1/admin/earning/user-plans/:subscriptionId/edit-history */
  getEarningUserPlanEditHistory: (subscriptionId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningUserPlanEditHistory(subscriptionId)),

  /** Check if user already has an active stake on a plan. Params: { emailId, planId } */
  checkExistingStake: (params) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningCheckExistingStake, {}, params),

  /** Search users for admin subscribe. Params: q or search, limit (default 10, max 20) */
  searchEarningUsers: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningSearchUsers, {}, params),

  /** Staking full report — list: { page, limit≤20, user, status, currency_id, plan_id, from, to }; detail: { subscription_id } */
  getStakingFullReport: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.earningStakingFullReport, {}, params),

  /** User-wise staking statement list. Params: page, limit≤50, user, status, currency_id, plan_id, from, to */
  getStakingStatement: (params = {}) =>
    apiGetWithMeta(ApiConfig.baseAdmin + ApiConfig.earningStakingStatement, {}, params),

  /**
   * One user's staking statement (summary + plans + ledger).
   * Params: status, currency_id, plan_id, subscription_id, from, to, include_pending, statement_page, statement_limit≤100
   */
  getUserStakingStatement: (userId, params = {}) =>
    apiGetWithMeta(
      ApiConfig.baseAdmin + ApiConfig.earningStakingStatementByUser(userId),
      {},
      params
    ),

  /**
   * Update user earning subscription.
   * Body: { userId, invested_amount, status, notes? }
   */
  updateEarningUserPlan: (subscriptionId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.earningUserPlanById(subscriptionId), body),

  /** PUT subscription-level payout status. Body: { payout_status: "active"|"inactive", userId? } */
  updateEarningUserPlanPayoutStatus: (subscriptionId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.earningUserPlanPayoutStatus(subscriptionId), body),

  /**
   * Admin subscribes a user to an earning/staking package.
   * Body: { planId, investAmount, walletType, emailId (email | phone | UUID), type: "offline" | "by_user_wallet" }
   */
  subscribeEarningPackageByAdminForUser: (body) =>
    apiPost(ApiConfig.earningSubscribePackageByAdminForUser, body),

  /** Cancel user staking subscription by admin. Body: { stakingId, userId } */
  cancelEarningSubscriptionByAdmin: (body) =>
    apiPost(ApiConfig.earningCancelSubscriptionByAdmin, body),

  /** GET ZILN pricing (usdt_price, zil_price, is_configured) */
  getZilPricing: () => apiGet(ApiConfig.adminZilPricing),

  /** PUT ZILN pricing. Body: { usdt_price, zil_price } */
  updateZilPricing: (body) => apiPut(ApiConfig.adminZilPricing, body),

  /** POST USDT → ZILN convert. Body: { usdt_amount } */
  convertZilPricing: (body) => apiPost(ApiConfig.adminZilPricingConvert, body),

  /** Get platform settings (features: enabled, scheduled_at, scheduled_action) */
  getPlatformConfig: () => apiGet(ApiConfig.baseAdmin + ApiConfig.platformConfig),

  /** Update platform settings (instant: { key: bool } or scheduled: { key: { enabled, scheduled_at, scheduled_action } }) */
  updatePlatformConfig: (body) => apiPut(ApiConfig.baseAdmin + ApiConfig.platformConfig, body),

  /** Cancel scheduled change for a feature */
  cancelPlatformSchedule: (feature) =>
    apiDelete(ApiConfig.baseAdmin + ApiConfig.platformCancelSchedule(feature)),

  /** Get bonus and referral rewards settings */
  getOtherSettings: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.otherSettings),

  /** Update bonus and referral rewards settings */
  updateOtherSettings: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.otherSettings, body),

  /** Brokerage: earners list — params: page, limit */
  getBrokerageEarners: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.brokerageEarners, {}, params),

  /** Brokerage: earner details by userId — params: page, limit, level, currency_id */
  getBrokerageEarnerDetails: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.brokerageEarnerById(userId), {}, params),

  /** Referral: referrers list — params: page, limit, search */
  getReferralReferrers: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralReferrers, {}, params),

  /** Referral: users commission overview (income, uplines, downline sources) — params: page, limit, search, account_status, kyc_status, userId */
  getReferralUsersCommissionOverview: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralUsersCommissionOverview, {}, params),

  /** Referral: all reward logs — params: page, limit, search */
  getReferralRewardLogs: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralRewardLogs, {}, params),

  /** Referral: user summary by userId */
  getReferralUserSummary: (userId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralUserSummary(userId)),

  /** Referral: direct invitees by userId */
  getReferralUserInvitees: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralUserInvitees(userId), {}, params),

  /** Referral: earning logs by userId */
  getReferralUserEarningLogs: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralUserEarningLogs(userId), {}, params),

  /** Referral: full user network (levels, earnings, commission history) — params: level (1–5) */
  getReferralUserNetwork: (userId, params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralUserNetwork(userId), {}, params),

  /** GET referral/downline-view/search — query: search. No body. */
  searchDownlineView: (search) =>
    apiGetWithMeta(ApiConfig.baseAdmin + ApiConfig.downlineViewSearch, {}, { search }),

  /** GET referral/downline-view/:userId — query: level optional. No body. */
  getDownlineView: (userId, params = {}) =>
    apiGetWithMeta(ApiConfig.baseAdmin + ApiConfig.downlineView(userId), {}, params, 120000),

  /** GET referral/downline-view/:userId/export — Excel file. No body. */
  downloadDownlineView: (userId, params = {}) =>
    apiGetBlob(ApiConfig.baseAdmin + ApiConfig.downlineViewExport(userId), params, 120000),

  /** Referral: signup/airdrop prerequisite task progress — params: page, limit, search */
  getReferralSignupTaskProgress: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralSignupTaskProgress, {}, params),

  /** Referral: users reward / bonus funds details — params: page, limit, search */
  getReferralRewardDetails: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralRewardDetails, {}, params),

  /** Referral: approve user bonus funds. Body: { mode: "instant" | "vesting" } */
  approveReferralFunds: (userId, body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.referralApproveFunds(userId), body),

  /** List referral release plans */
  getReferralReleasePlans: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralReleasePlans),

  /** Get one referral release plan */
  getReferralReleasePlanById: (planId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.referralReleasePlanById(planId)),

  /** Create plan. Body: { name, setActive?, rounds: [{ round, fromMonth, toMonth, percentage }] } */
  createReferralReleasePlan: (body) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.referralReleasePlans, body),

  /** Update plan (partial). Body: { name? }, { rounds? }, { setActive: true } or { isActive: bool }, or combined */
  updateReferralReleasePlan: (planId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.referralReleasePlanById(planId), body),

  /** Delete plan */
  deleteReferralReleasePlan: (planId) =>
    apiDelete(ApiConfig.baseAdmin + ApiConfig.referralReleasePlanById(planId)),

  /** Manual release for a round. Body: { round: number, ignoreMonthWindow?: boolean } */
  releaseReferralRoundNow: (body) =>
    apiPostWithMeta(ApiConfig.baseAdmin + ApiConfig.referralReleaseRoundNow, body),

  /** Poll manual round-release job. */
  getReferralReleaseRoundNowStatus: (params = {}) =>
    apiGetWithMeta(
      ApiConfig.baseAdmin + ApiConfig.referralReleaseRoundNowStatus,
      {},
      params
    ),

  /** User list for round-release ledger. Params: page, limit≤50, user, status */
  getReferralReleaseUsers: (params = {}) =>
    apiGetWithMeta(ApiConfig.baseAdmin + ApiConfig.referralReleasePlanUsers, {}, params),

  /** One user's vesting rounds + staking-wallet history */
  getReferralReleaseUserById: (userId, params = {}) =>
    apiGetWithMeta(
      ApiConfig.baseAdmin + ApiConfig.referralReleasePlanUserById(userId),
      {},
      params
    ),

  /**
   * GET /v1/admin/referral-release-plans/round-reports
   * Paid users for one round. Params: round (required), page, limit≤100, user
   */
  getReferralReleaseRoundReports: (params = {}) =>
    apiGetWithMeta(ApiConfig.baseAdmin + ApiConfig.referralReleaseRoundReports, {}, params),

  // ========= Exchange (admin) =========
  /** Trading commission history. Params: { page, limit } */
  getAdminTradingCommission: (params = {}) =>
    apiGet(ApiConfig.adminExchangeTradingCommission, {}, params),

  /** Withdrawal fee records. Params: { page, limit } */
  getAdminWithdrawalFees: (params = {}) =>
    apiGet(ApiConfig.adminExchangeWithdrawalFees, {}, params),

  /** P2P fee aggregates per user. Params: { page, limit } */
  getAdminP2pFees: (params = {}) =>
    apiGet(ApiConfig.adminExchangeP2pFees, {}, params),

  /** Futures commission records. Params: { page, limit } */
  getAdminFuturesCommission: (params = {}) =>
    apiGet(ApiConfig.adminExchangeFuturesCommission, {}, params),

  /** Exchange wallet types list. Returns string[] like main/spot/p2p/futures/swap/earning */
  getAdminExchangeWalletTypes: () =>
    apiGet(ApiConfig.adminExchangeAllWalletTypes),

  /** Exchange all coins list for dropdowns */
  getAdminExchangeAllCoins: () =>
    apiGet(ApiConfig.adminExchangeAllCoins),

  /** Wallet details by coin + wallet type. Body: { coinName, walletType } */
  getAdminExchangeWalletDetailsByCoin: (coinName, walletType) =>
    apiPost(ApiConfig.adminExchangeWalletDetailsByCoin, { coinName, walletType }),

  /** Admin debit/credit in exchange wallet */
  adminExchangeDebitCreditByAdmin: (body) =>
    apiPost(ApiConfig.adminExchangeDebitCreditByAdmin, body),

  /** Cancel a spot order. Body: { order_id } (Mongo _id of the order document) */
  cancelAdminSpotOrder: (body) =>
    apiPostWithMeta(ApiConfig.adminExchangeCancelOrder, body),

  /** Admin debit/credit transactions list. Params: { page, limit } */
  getAdminDebitCreditTransactions: (params = {}) =>
    apiGet(ApiConfig.adminExchangeAllAdminDebitCreditTransactions, {}, params),

  /** Add announcement category. Body: { title, description } */
  addAnnouncementCategory: (body) =>
    apiPost(ApiConfig.adminExchangeAddAnnouncementCategory, body),

  /** Announcement categories listing */
  getAnnouncementCategoryListing: () =>
    apiGet(ApiConfig.adminExchangeAnnouncementCategoryListing),

  /** Create new announcement. Body: { title, description, announcementCategoryId } */
  createNewAnnouncement: (body) =>
    apiPost(ApiConfig.adminExchangeCreateNewAnnouncement, body),

  /** Admin announcement listing */
  getAllAnnouncementListAdmin: () =>
    apiGet(ApiConfig.adminExchangeAllAnnouncementListAdmin),

  /** Update announcement status. Body: { id, status } */
  updateAnnouncementStatus: (id, status) =>
    apiPost(ApiConfig.adminExchangeUpdateAnnouncementStatus, { id, status }),

  /** Delete announcement by id */
  deleteAnnouncement: (id) =>
    apiDelete(ApiConfig.adminExchangeDeleteAnnouncement(id)),

  /** Find users by email for notification targeting. Body: { emailId } */
  findUserByEmailForNotification: (emailId) =>
    apiPost(ApiConfig.adminSystemFindUserByEmailForNotification, { emailId }),

  /** Send notification to single user. Body: { title, message, userId, link? } */
  sendNotificationToSingleUser: (body) =>
    apiPost(ApiConfig.adminNotificationSendToSingleUser, body),

  /** Send notification to bulk users. Body: { title, message, userIds, link? } */
  sendNotificationToBulkUsers: (body) =>
    apiPost(ApiConfig.adminNotificationSendToBulkUsers, body),

  /** Send notification to all users. Body: { title, message, link? } */
  sendNotificationToAll: (body) =>
    apiPost(ApiConfig.adminNotificationSendToAll, body),

  /** Get all notifications list. Supports pagination params if backend accepts */
  getAllNotificationList: (params = {}) =>
    apiGet(ApiConfig.adminNotificationGetAllList, {}, params),

  /** Update notification status. Body: { id, isActive } */
  updateNotificationStatus: (id, isActive) =>
    apiPost(ApiConfig.adminNotificationUpdateStatus, { id, isActive }),

  /** Delete notification by id */
  deleteNotification: (id) =>
    apiDelete(ApiConfig.adminNotificationDelete(id)),

  /** Update APK version with file upload. FormData: { version, apk } */
  updateApk: (formData) =>
    apiPostForm(ApiConfig.adminApkUpdate, formData),

  /** Current APK metadata (version, path, updatedAt) */
  getApk: () => apiGet(ApiConfig.adminApkGet),

  /** Find users by email for send-email recipient field. Body: { emailId } */
  findUserByEmailForSendEmail: (emailId) =>
    apiPost(ApiConfig.adminEmailFindUserByEmailForSendEmail, { emailId }),

  /** Send email to a user. FormData: emailId, subject, message, attachments (files, optional) */
  sendEmailToUser: (formData) =>
    apiPostForm(ApiConfig.adminEmailSendToUser, formData),

  /** Admin sent email list. Params supported if backend provides pagination/filtering */
  getAdminEmailList: (params = {}) =>
    apiGet(ApiConfig.adminEmailGetAdminEmailList, {}, params),

  /** Get all support tickets for support management page. Params: page, limit */
  getAllSupportTickets: (params = {}) =>
    apiGet(ApiConfig.supportGetAllTickets, {}, params),

  /** Update support ticket status. status: Closed | Resolved */
  updateSupportTicketStatus: (ticket_id, status) =>
    apiPost(ApiConfig.supportUpdateTicketStatus, { ticket_id, status }),

  /** Reply to support ticket from admin side (replyBy = 0) */
  replySupportTicket: (ticket_id, query, replyBy = 0) =>
    apiPost(ApiConfig.supportReplyTicket, { ticket_id, query, replyBy }),

  // ========= Launchpad =========
  /** Get currencies for launchpad (base & quote selection) */
  getLaunchpadCurrencies: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadCurrencies),

  /** Create launchpad (multipart/form-data: logo, banner, fields per API) */
  createLaunchpad: (formData) =>
    apiPostForm(ApiConfig.baseAdmin + ApiConfig.launchpadCreate, formData),

  /** List launchpads (GET launchpad/list). Params: { page, limit, status } */
  getLaunchpadListing: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadListing, {}, params),

  /** Update launchpad (multipart FormData — POST /admin/launchpad/edit/:id) */
  updateLaunchpad: (id, formData) =>
    apiPostForm(ApiConfig.launchpadUpdate(id), formData),

  /** Update launchpad status. Body: { id, status } -> /admin/launchpad/update-launchpad-status */
  updateLaunchpadStatus: (id, status) =>
    apiPost(ApiConfig.launchpadUpdateStatus, { id, status }),

  /** Update launchpad fund status. Body: { id, status: FILLED | FAILED | CANCELLED } */
  updateLaunchpadFundStatus: (id, status) =>
    apiPost(ApiConfig.launchpadUpdateFundStatus, { id, status }),

  /** Per-launchpad sales stats (GET /admin/launchpad/all-launchpads-stats) */
  getLaunchpadAllLaunchpadsStats: () =>
    apiGet(ApiConfig.launchpadAllLaunchpadsStats),

  /** Get all launchpads summary (aggregate) */
  getLaunchpadSummary: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadSummary),

  /** Dashboard counts: total, upcoming, live, ended (GET launchpad/count) */
  getLaunchpadCount: () => apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadCount),

  /** Transactions for a launchpad (GET launchpad/transactions/:launchpadId) */
  getLaunchpadTransactions: (launchpadId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.launchpadTransactions(launchpadId)),

  // ========= Withdrawals =========
  getPendingWithdrawals: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.pendingWithdrawalRequest),

  getCompletedWithdrawals: (skip = 0, limit = 100) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.completeWithdrawalRequest, { skip, limit }),

  getRejectedWithdrawals: (skip = 0, limit = 100) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.rejectWithdrawalRequest, { skip, limit }),

  updateWithdrawalStatus: (payload) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.updateWithdrawalStatus, payload),

  getAllCryptoWithdrawals: () => apiGet(ApiConfig.getWithdrawalHistory),

  getAllInrWithdrawals: () => apiGet(ApiConfig.getInrWithdrawalHistory),

  /** GET sub-admins/permissions — permission catalog for create/edit forms */
  getSubAdminPermissions: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.subAdminPermissions),

  /** GET sub-admins — params: page, limit, search, status */
  getSubAdmins: (params = {}) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.subAdmins, {}, params),

  /** GET sub-admins/:id */
  getSubAdminById: (subAdminId) =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.subAdminById(subAdminId)),

  /** POST sub-admins — body: name, email, mobile_number, password, permissions */
  createSubAdmin: (body) => apiPost(ApiConfig.baseAdmin + ApiConfig.subAdmins, body),

  /** PUT sub-admins/:id */
  updateSubAdmin: (subAdminId, body) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.subAdminById(subAdminId), body),

  /** PUT sub-admins/:id/status — body: { status: "active" | "inactive" } */
  updateSubAdminStatus: (subAdminId, status) =>
    apiPut(ApiConfig.baseAdmin + ApiConfig.subAdminStatus(subAdminId), { status }),

  /** GET /v1/admin/withdrawal-status */
  getAdminWithdrawalStatus: () =>
    apiGet(ApiConfig.baseAdmin + ApiConfig.adminWithdrawalStatus),

  /** POST /v1/admin/withdrawal-status — body: { withdrawal_status: boolean } */
  setAdminWithdrawalStatus: (withdrawal_status) =>
    apiPost(ApiConfig.baseAdmin + ApiConfig.adminWithdrawalStatus, { withdrawal_status }),
};

export default AdminService;
